import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { MegaphoneIcon } from "@phosphor-icons/react";

import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import AnnouncementItem from "./AnnouncementItem";

interface Announcement {
  id: string;
  title: string;
  message: string;
  author: string;
  date: string;
  status: "published" | "scheduled" | "draft";
  targetAudience: string;
  targetDetails?: string;
  created?: string;
  lastModified?: string;
  expirationDate?: string;
}

interface AnnouncementsListProps {
  announcements: Announcement[];
  activeFilter: string;
  targetAudience: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onPublish?: (id: string) => void;
  onReschedule?: (id: string) => void;
  onCreateNew: () => void;
}

const AnnouncementsList: React.FC<AnnouncementsListProps> = ({
  announcements,
  activeFilter,
  targetAudience,
  onEdit,
  onDelete,
  onPublish,
  onReschedule,
  onCreateNew,
}) => {
  const filteredAnnouncements = announcements.filter((announcement) => {
    // Filter by status
    if (activeFilter !== "all" && announcement.status !== activeFilter) {
      return false;
    }

    // Filter by target audience
    if (targetAudience !== "all" && announcement.targetAudience !== targetAudience) {
      return false;
    }

    return true;
  });

  if (filteredAnnouncements.length === 0) {
    return (
      <View style={styles.emptyState}>
        <View style={styles.emptyIcon}>
          <MegaphoneIcon size={48} color="#9CA3AF" />
        </View>
        <CustomText text="No announcements found" textType="display" size="s" weight="bold" style={styles.emptyTitle} />
        <CustomText text="Try adjusting your filters or create a new announcement." style={styles.emptyDescription} />
        <CustomButton
          text="Create Announcement"
          leftIcon={<MegaphoneIcon size={16} color="#FFFFFF" />}
          onPress={onCreateNew}
          styleType="primary"
          style={styles.createButton}
        />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false} tabIndex={0}>
      <View style={styles.list}>
        {filteredAnnouncements.map((announcement) => (
          <AnnouncementItem
            key={announcement.id}
            announcement={announcement}
            onEdit={onEdit}
            onDelete={onDelete}
            onPublish={onPublish}
            onReschedule={onReschedule}
          />
        ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  list: {
    paddingVertical: 24,
  },
  emptyState: {
    marginVertical: 24,
    padding: 48,
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  emptyIcon: {
    marginBottom: 16,
  },
  emptyTitle: {
    color: "#1F2937",
    marginBottom: 8,
  },
  emptyDescription: {
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 20,
  },
  createButton: {
    backgroundColor: "#3E68FE",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
});

export default AnnouncementsList;
